import { useQuery } from '@tanstack/react-query';
import { adminApi, type Audit } from '@l2u/web-shared';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { BELOW_LG_QUERY, useIsMobile } from '../lib/useIsMobile.ts';
import {
  NAV_BADGE_REFRESH,
  NO_REVIEW_PENDING,
  pendingByPath,
  reviewPendingFrom,
  type ReviewPending,
} from '../lib/pendingWork.ts';
import { isOpenAudit, MY_AUDITS_QUERY_KEY } from './myAuditsFilter.ts';
import { adminQueries } from '../lib/adminQueries.ts';

/**
 * The counts behind the nav's badges (lib/pendingWork.ts): the review queue
 * and the needs-review verifications for the More screen's rows, the signed-in
 * user's open audits for the Audits tab, and the drivers and assets in the
 * review queue for the Drivers/Assets rows (sidebar, More screen, Assets tab).
 *
 * Mounted by AdminShell (the tab bar and sidebar) and More.tsx (the rows). Both
 * read the same query keys, so react-query serves them from one fetch — and the
 * More screen mounting with a stale cache is what refreshes the tab's number too.
 *
 * The verification and audit lists are fetched only below `lg`, where the tab
 * bar exists: none of the endpoints has a count-only mode, so each count is a
 * list fetch, and at `lg`+ nothing renders them. The review queue is fetched at
 * every width, because the desktop sidebar's Drivers and Assets counts read it.
 */
export function usePendingWork() {
  const { user, activeTeamRegion } = useAdminAuth();
  const mobile = useIsMobile(BELOW_LG_QUERY) && !!user;

  const reviewQueue = useQuery({
    // The same request the Queue page makes with its filters at their defaults
    // (every entity, every assignee), region included — so the number is the
    // length of the list the row opens.
    ...adminQueries.reviewQueue(activeTeamRegion),
    select: reviewPendingFrom,
    // AdminShell never remounts: without a refresh a newly submitted answer
    // only reached the sidebar's Drivers/Assets counts after a reload — the
    // same reason the corrective-actions badge re-asks.
    ...NAV_BADGE_REFRESH,
    enabled: !!user,
  });

  const verifications = useQuery({
    // The Verifications page's default tab — the same cache entry, so the row
    // and the page it opens never disagree and opening it is a cache hit.
    ...adminQueries.verifications('needs_review'),
    select: (res) => res.verifications.length,
    enabled: mobile,
  });

  const openAudits = useQuery({
    // Shared with MyAudits.tsx and the dashboard feed; the list lands in the
    // cache as-is and `select` only shapes what this observer sees.
    queryKey: MY_AUDITS_QUERY_KEY,
    queryFn: () => adminApi.get<{ audits: Audit[] }>('/api/audits/mine'),
    select: (res) => res.audits.filter((a) => isOpenAudit(a.status)).length,
    enabled: mobile,
  });

  // Unknown (loading, failed) is absent, never zero — see pendingByPath.
  const queue = reviewQueue.isSuccess ? reviewQueue.data : undefined;
  return {
    byPath: pendingByPath({
      reviewQueue: queue?.total,
      verifications: verifications.isSuccess ? verifications.data : undefined,
      drivers: queue?.drivers.size,
    }),
    // Same unknown-is-absent rule as pendingByPath.
    openAudits: openAudits.isSuccess ? openAudits.data : 0,
    reviewDrivers: queue?.drivers.size,
    reviewAssets: queue?.assets.size,
  };
}

/**
 * The drivers and assets the review queue lists, for the lists' "Needs review"
 * pills. The SAME cache entry as usePendingWork's queue (adminQueries.reviewQueue,
 * keyed on the active region like the lists themselves), so a pill and the
 * sidebar count can never disagree, and a list page adds no request when the
 * shell has already fetched it.
 *
 * Empty while loading or failed: a missing pill is honest about not knowing,
 * a wrong one sends someone to review nothing.
 */
export function useReviewPending(): ReviewPending {
  const { user, activeTeamRegion } = useAdminAuth();
  const query = useQuery({
    ...adminQueries.reviewQueue(activeTeamRegion),
    select: reviewPendingFrom,
    enabled: !!user,
  });
  return query.isSuccess ? query.data : NO_REVIEW_PENDING;
}
